/**
 * SW2-2409. Which documents-log rows can be restored.
 *
 * Restoring a replaced version moves the map's features, notes and change log back
 * from its successor (the version that replaced it). Replacing a map carries all of
 * them forward to the current version, so once the successor has itself been replaced
 * it holds nothing: restoring would bring the old version back empty beside a live
 * current one. The backend refuses that restore (409); the table offers Restore only
 * where it works, and restoring the newer version first makes the older one restorable.
 *
 * Site maps only (the "Active Site Maps" folder), where the change log moves with the
 * map. Other documents restore as they always have.
 */

// doc_types.id of "Active Site Maps".
const ACTIVE_SITE_MAPS_DOC_TYPE_ID = 1;

interface Successor {
  status?: string | null;
}

interface DocumentsLogRow {
  status?: string | null;
  replaced_by?: number | null;
  project_upload?: Successor | null;
  project_doc_type?: { doc_type_id?: number | null } | null;
}

export const RESTORE_NEWER_FIRST =
  'Only the version immediately before the current one can be restored. Restore the newer version first.';

/**
 * True when the row's successor has itself been replaced, so restoring it would come back
 * empty. Status alone decides, matching the backend: some rows restored by an older flow
 * are live and current but still carry a stale replaced_by.
 */
export const successorWasReplaced = (row: DocumentsLogRow): boolean => {
  const successor = row.project_upload;

  const isSiteMap =
    row.project_doc_type?.doc_type_id === ACTIVE_SITE_MAPS_DOC_TYPE_ID;

  if (
    !isSiteMap ||
    row.status !== 'Replaced' ||
    !row.replaced_by ||
    !successor
  ) {
    return false;
  }

  return successor.status === 'Replaced';
};
